import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  TextField,
  Checkbox,
  Button,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { CheckBoxOutlineBlank, CheckBox as CheckBoxIcon } from '@mui/icons-material';

import { profileInfoAction } from '../../redux/profile/Reducer';

import { ProfileInfoHeader } from '../shared/ProfileInfoHeader';
import { FormPageNavBtns } from '../shared/FormPageNavBtns';
import { FormCardWrap } from '../shared/FormCardWrap';
import { formCircleBtnNavLink } from '../../utils/generalFunc';
import { useStyles } from './style';

const nextBtnDisabledFunc = (formik) => (Object.keys(formik.errors).length !== 0);

const label = { inputProps: { 'aria-label': 'Checkbox' } };

export const PrimaryCareProviderForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile.profilInfo);

  const formik = useFormik({
    initialValues: {
      primaryCarePhy: profile?.PrimaryCarePhysician || '',
      phNo: profile?.PrimaryCarePhone || '',
      primaryCareProviderAddr: profile?.PrimaryCareAddress || '',
      isAverTreatProvider: false,
      treatProviderName: profile?.TreatmentProviderName || '',
      treatProviderPh: profile?.TreatmentProviderPhone || '',
      treatProviderAddr: profile?.TreatmentProviderAddress || '',
      treatCounselorName: profile?.TreatmentCounselorName || '',
      treatCounselorPh: profile.TreatmentCounselorPhone || '',
    },
    validationSchema: yup.object({
      primaryCarePhy: yup
        .string()
        .trim(),
      phNo: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/,
          'Enter a valid phone number'
        ),
      primaryCareProviderAddr: yup
        .string()
        .trim(),
      isAverTreatProvider: yup.bool(),
      treatProviderName: yup
        .string()
        .trim(),
      treatProviderPh: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/,
          'Enter a valid phone number'
        ),
      treatProviderAddr: yup
        .string()
        .trim(),
      treatCounselorName: yup
        .string()
        .trim(),
      treatCounselorPh: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/,
          'Enter a valid phone number'
        ),
    }),
    onSubmit: (data) => {
      // go into next page if statement if button is enabled
      if (!nextBtnDisabledFunc(formik)) {
        dispatch(profileInfoAction({
          ...profile,
          PrimaryCarePhysician: data?.primaryCarePhy,
          PrimaryCarePhone: data?.phNo,
          PrimaryCareAddress: data?.primaryCareProviderAddr,
          TreatmentProviderName: data?.treatProviderName,
          TreatmentProviderPhone: data?.treatProviderPh,
          TreatmentProviderAddress: data?.treatProviderAddr,
          TreatmentCounselorName: data?.treatCounselorName,
          TreatmentCounselorPhone: data.treatCounselorPh,
        }));
        history.push('/emergency-info');
      }
    },
  });

  const onDotClick = (dotNum) => {
    formCircleBtnNavLink(history, dotNum);
  };

  return (
    <Container maxWidth="md" className={classes.personalInfoContainer}>
      <ProfileInfoHeader />
      <FormCardWrap cardsBgNumber={1}>
        <form onSubmit={formik.handleSubmit} className="formContainer">
          <Box className={classes.personalContentWrap}>
            <Box className={classes.formPageNumber}>3/4</Box>
            <Box className={classes.contentTitleWrap}>
              <Box component="span" className={`${classes.contentTitleLine} ${classes.titleLine1}`} />
              <Box className={classes.contentTitle}>Primary Care Provider Info</Box>
              <Box component="span" className={`${classes.contentTitleLine} ${classes.titleLine2}`} />
            </Box>
            <Box className={`${classes.phyNamePhContainerStyle} ${classes.subTextColor}`}>
              <Box className={classes.phyNameContainerStyle}>
                <Box className="commonLabel" component="label">&nbsp;Primary Care Physician</Box>
                <TextField
                  name="primaryCarePhy"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('primaryCarePhy')}
                  value={formik.values.primaryCarePhy || ''}
                  className="inputFormTopMargin"
                  id="primaryCarePhy"
                  placeholder="Primary Care Physician"
                  variant="outlined"
                />
              </Box>
              <Box className={classes.fullWidth}>
                <Box className="commonLabel" component="label">&nbsp;Phone</Box>
                <TextField
                  name="phNo"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('phNo')}
                  value={formik.values.phNo || ''}
                  className="inputFormTopMargin"
                  id="phNo"
                  placeholder="Phone"
                  variant="outlined"
                />
                {formik.touched.phNo && formik.errors.phNo && (
                  <div className={classes.errorText}>{formik.errors.phNo}</div>
                )}
              </Box>
            </Box>
            <Box className={`${classes.phyAddrContainer} ${classes.subTextColor}`}>
              <Box className="commonLabel" component="label">&nbsp;Primary Care Provider Address</Box>
              <TextField
                name="primaryCareProviderAddr"
                onChange={formik.handleChange}
                onBlur={() => formik.setFieldTouched('primaryCareProviderAddr')}
                value={formik.values.primaryCareProviderAddr || ''}
                className="inputFormTopMargin"
                id="outlined-basic"
                placeholder="Primary Care Provider Address"
                variant="outlined"
              />
            </Box>
            <Box className={`${classes.contentTitleWrap} ${classes.treatmentTitleHeader}`}>
              <Box component="span" className={`${classes.contentTitleLine} ${classes.titleLine1}`} />
              <Box className={classes.contentTitle}>Treatment Provider Info</Box>
              <Box component="span" className={`${classes.contentTitleLine} ${classes.titleLine2}`} />
            </Box>
            <Box className={classes.checkboxContainer}>
              <Checkbox
                name="isAverTreatProvider"
                onChange={formik.handleChange}
                checked={formik.values.isAverTreatProvider || false}
                {...label}
                icon={<CheckBoxOutlineBlank />}
                checkedIcon={<CheckBoxIcon />}
              />
              <Box className={classes.checkboxText} component="span">Averhealth is my treatment provider</Box>
            </Box>
            <Box className={`${classes.phyNamePhContainerStyle} ${classes.subTextColor} ${classes.treatmentProviderWrap}`}>
              <Box className={classes.phyNameContainerStyle}>
                <Box className={`commonLabel ${classes.treatmentProviderLabel}`} component="label">&nbsp;Treatment Provider Name</Box>
                <TextField
                  name="treatProviderName"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('treatProviderName')}
                  value={formik.values.treatProviderName || ''}
                  className="inputFormTopMargin"
                  id="treatProviderName"
                  placeholder="Treatment Provider Name"
                  variant="outlined"
                  disabled={formik.values.isAverTreatProvider}
                />
              </Box>
              <Box className={classes.fullWidth}>
                <Box className="commonLabel" component="label">&nbsp;Phone</Box>
                <TextField
                  name="treatProviderPh"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('treatProviderPh')}
                  value={formik.values.treatProviderPh || ''}
                  className="inputFormTopMargin"
                  id="treatProviderPh"
                  placeholder="Phone"
                  variant="outlined"
                  disabled={formik.values.isAverTreatProvider}
                />
                {formik.touched.treatProviderPh && formik.errors.treatProviderPh && (
                  <div className={classes.errorText}>{formik.errors.treatProviderPh}</div>
                )}
              </Box>
            </Box>
            <Box className={`${classes.phyAddrContainer} ${classes.subTextColor}`}>
              <Box className="commonLabel" component="label">&nbsp;Treatment Provider Address</Box>
              <TextField
                name="treatProviderAddr"
                onChange={formik.handleChange}
                onBlur={() => formik.setFieldTouched('treatProviderAddr')}
                value={formik.values.treatProviderAddr || ''}
                className="inputFormTopMargin"
                id="treatProviderAddr"
                placeholder="Treatment Provider Address"
                variant="outlined"
                disabled={formik.values.isAverTreatProvider}
              />
            </Box>
            <Box className={`${classes.phyNamePhContainerStyle} ${classes.subTextColor}`}>
              <Box className={classes.phyNameContainerStyle}>
                <Box className={`commonLabel ${classes.treatmentCouncelorLabel}`} component="label">&nbsp;Treatment Counselor Name</Box>
                <TextField
                  name="treatCounselorName"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('treatCounselorName')}
                  value={formik.values.treatCounselorName || ''}
                  className="inputFormTopMargin"
                  id="treatCounselorName"
                  placeholder="Treatment Counselor Name"
                  variant="outlined"
                  disabled={formik.values.isAverTreatProvider}
                />
              </Box>
              <Box className={classes.fullWidth}>
                <Box className="commonLabel" component="label">&nbsp;Phone</Box>
                <TextField
                  name="treatCounselorPh"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('treatCounselorPh')}
                  value={formik.values.treatCounselorPh || ''}
                  className="inputFormTopMargin"
                  id="outlined-basic"
                  placeholder="Phone"
                  variant="outlined"
                  disabled={formik.values.isAverTreatProvider}
                />
                {formik.touched.treatCounselorPh && formik.errors.treatCounselorPh && (
                  <div className={classes.errorText}>{formik.errors.treatCounselorPh}</div>
                )}
              </Box>
            </Box>
            {/* end */}
            <Box className={classes.buttonsNavContainer}>
              <Button onClick={() => history.push('/insurance-info')} variant="contained" color="secondary">
                Back
              </Button>
              <FormPageNavBtns formPageNumber={4} activePgNum={3} onDotClick={onDotClick} />
              <Button
                className={nextBtnDisabledFunc(formik) ? classes.btnDisabled : ''}
                type="submit"
                variant="contained"
                color="primary"
              >
                Next
              </Button>
            </Box>
          </Box>
        </form>
      </FormCardWrap>
    </Container>
  );
};
